import React, { useEffect, useState } from 'react';
import { Check, Loader2, UserPlus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { client } from '../../lib/amplifyClient';
import {
  acceptTherapistReferral,
  declineTherapistReferral,
  getTherapistReferral,
  type TherapistReferralRow,
} from '../../lib/therapistReferral';
import ParentTherapistPreviewModal from './ParentTherapistPreviewModal';

type Props = {
  referralId: string | null;
  onClose: () => void;
  onResolved?: () => void;
};

export default function ParentTherapistReferralDecideModal({
  referralId,
  onClose,
  onResolved,
}: Props) {
  const { user } = useAuth();
  const [row, setRow] = useState<TherapistReferralRow | null>(null);
  const [fromLabel, setFromLabel] = useState('Therapist');
  const [toLabel, setToLabel] = useState('Therapist');
  const [kidName, setKidName] = useState('your child');
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  useEffect(() => {
    if (!referralId) {
      setRow(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const referral = await getTherapistReferral(referralId);
        if (cancelled) return;
        if (!referral) {
          setError('Referral not found.');
          setRow(null);
          return;
        }
        setRow(referral);
        const [fromUser, toUser, kid] = await Promise.all([
          client.models.User.get({ id: referral.therapistId }, { authMode: 'apiKey' }),
          client.models.User.get({ id: referral.therapist2Id }, { authMode: 'apiKey' }),
          client.models.KidProfile.get({ id: referral.kidProfileId }, { authMode: 'apiKey' }),
        ]);
        if (cancelled) return;
        setFromLabel(
          `${fromUser.data?.fName || ''} ${fromUser.data?.lName || ''}`.trim() ||
            fromUser.data?.email ||
            'Therapist',
        );
        setToLabel(
          `${toUser.data?.fName || ''} ${toUser.data?.lName || ''}`.trim() ||
            toUser.data?.email ||
            'Therapist',
        );
        setKidName((kid.data?.name || '').trim() || 'your child');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load referral.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [referralId]);

  if (!referralId) return null;

  const pending = !row?.status || row.status === 'PENDING';

  const runAccept = () => {
    if (!user?.id || !row) return;
    setActing(true);
    setError(null);
    void acceptTherapistReferral(row.id, user.id)
      .then(() => {
        setPreviewId(null);
        onResolved?.();
        onClose();
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Accept failed.'))
      .finally(() => setActing(false));
  };

  const runDecline = () => {
    if (!user?.id || !row) return;
    setActing(true);
    setError(null);
    void declineTherapistReferral(row.id, user.id)
      .then(() => {
        setPreviewId(null);
        onResolved?.();
        onClose();
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Decline failed.'))
      .finally(() => setActing(false));
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[1600] flex items-end justify-center bg-slate-900/45 p-4 sm:items-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="parent-referral-decide-title"
        onClick={(e) => {
          if (e.target === e.currentTarget && !acting) onClose();
        }}
      >
        <div
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
              <UserPlus className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 pr-8">
              <h2 id="parent-referral-decide-title" className="text-lg font-semibold text-slate-900">
                Therapist referral
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">Review and Accept or Decline for {kidName}.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={acting}
              className="absolute right-3 top-3 rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            {loading ? (
              <p className="inline-flex items-center gap-2 py-6 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading…
              </p>
            ) : null}
            {error ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{error}</p>
            ) : null}
            {row && !loading ? (
              <>
                <p className="text-sm text-slate-800">
                  <button
                    type="button"
                    className="font-semibold text-indigo-700 hover:underline"
                    onClick={() => setPreviewId(row.therapistId)}
                  >
                    {fromLabel}
                  </button>{' '}
                  referred{' '}
                  <button
                    type="button"
                    className="font-semibold text-indigo-700 hover:underline"
                    onClick={() => setPreviewId(row.therapist2Id)}
                  >
                    {toLabel}
                  </button>
                </p>
                {row.note ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Note</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{row.note}</p>
                  </div>
                ) : null}
                {!pending ? (
                  <p className="text-sm font-medium text-slate-600">Status: {row.status}</p>
                ) : (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      disabled={acting || !user?.id}
                      onClick={runAccept}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      Accept
                    </button>
                    <button
                      type="button"
                      disabled={acting || !user?.id}
                      onClick={runDecline}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                      Decline
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <ParentTherapistPreviewModal
        therapistUserId={previewId}
        onClose={() => {
          if (!acting) setPreviewId(null);
        }}
        onAccept={pending && row && user?.id ? runAccept : undefined}
        onDecline={pending && row && user?.id ? runDecline : undefined}
        acting={acting}
        actionError={error}
      />
    </>
  );
}
